import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Activity, TrendingUp } from 'lucide-react';

const pct = (v, signed = true) => (v == null ? 'n/a' : `${signed && v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);

const RULES = [
  ['price_above_150_200', 'Price above the 150- and 200-day averages'],
  ['sma150_above_200', '150-day average above the 200-day'],
  ['sma200_rising_1m', '200-day average rising for a month'],
  ['sma50_above_150_200', '50-day average above the 150- and 200-day'],
  ['price_above_50', 'Price above the 50-day average'],
  ['above_52w_low_30pct', 'At least 30% above the 52-week low'],
  ['within_25pct_of_52w_high', 'Within 25% of the 52-week high'],
  ['rs_rank_70_plus', 'Relative-strength rank 70 or better'],
];

// Click-to-open explainer for the Minervini RS / Tight base row badges.
// Hover titles are invisible on phones, so both badges open this.
export default function MinerviniBadge({ ticker, m, kind }) {
  const [open, setOpen] = useState(false);
  if (!m) return null;
  const isRs = kind === 'rs';
  const chipClass = isRs
    ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border-sky-300/60 dark:border-sky-700/60'
    : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-300/60 dark:border-amber-700/60';
  const rules = m.rules || {};
  const passed = RULES.filter(([k]) => rules[k]).length;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide border hover:brightness-110 active:scale-95 transition-[filter,transform] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${chipClass}`}
        aria-label={isRs ? `Relative strength rank ${m.rs_rank ?? '—'} — tap for the Minervini breakdown` : 'Tight base — tap for the VCP proxy breakdown'}
        data-testid={isRs ? `screener-minervini-rs-badge-${ticker}` : `screener-minervini-tight-badge-${ticker}`}
      >
        {isRs ? `RS ${m.rs_rank ?? '—'}` : 'Tight base'}
      </button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent className="max-w-md w-[95vw] sm:w-auto max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`minervini-badge-modal-${ticker}`}>
          <DialogHeader>
            <DialogTitle className="flex items-start gap-2 pr-8 leading-snug">
              {isRs ? <TrendingUp className="w-5 h-5 mt-0.5 shrink-0 text-sky-500" /> : <Activity className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />}
              {ticker} · {isRs ? `Relative Strength ${m.rs_rank ?? '—'} of 99` : 'Tight base (VCP proxy)'}
            </DialogTitle>
            <DialogDescription>
              {isRs
                ? 'IBD-style 12-month relative strength — the 3-month return counted twice plus the 6-, 9- and 12-month returns — ranked against every non-dividend common stock DiviDrip scans. 99 = strongest.'
                : 'Mark Minervini\u2019s Volatility Contraction Pattern can only be confirmed on a chart. This badge flags the measurable part: price range and volume both drying up versus the 50-day baseline.'}
            </DialogDescription>
          </DialogHeader>

          {isRs ? (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  ['Off 52-wk high', pct(m.off_high_pct == null ? null : -m.off_high_pct, true)],
                  ['EPS YoY (latest qtr)', pct(m.eps_yoy_pct)],
                  ['Prior qtr YoY', pct(m.eps_prior_yoy_pct)],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-md border bg-muted/30 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">{k}</p>
                    <p className="font-mono text-sm font-bold mt-0.5">{v}</p>
                  </div>
                ))}
              </div>
              {m.eps_accelerating != null && (
                <p className={`text-xs font-semibold ${m.eps_accelerating ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`} data-testid={`minervini-accel-${ticker}`}>
                  {m.eps_accelerating ? 'Earnings growth is accelerating — the latest quarter\u2019s YoY growth beat the prior quarter\u2019s.' : 'Earnings growth is not accelerating this quarter (SEPA prefers speeding-up growth).'}
                </p>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">Trend Template · {passed} of 8 rules</p>
                <ul className="space-y-1">
                  {RULES.map(([k, label]) => (
                    <li key={k} className="flex items-center gap-2 text-xs">
                      <span className={`inline-flex w-4 h-4 items-center justify-center rounded-full text-[10px] font-bold ${rules[k] ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-rose-500/15 text-rose-600 dark:text-rose-400'}`}>{rules[k] ? '✓' : '✕'}</span>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-md border bg-muted/30 p-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">10-day range vs 50-day</p>
                <p className="font-mono text-sm font-bold mt-0.5">{m.tight_ratio != null ? `${Math.round(m.tight_ratio * 100)}%` : 'n/a'}</p>
                <p className="text-[10px] text-muted-foreground">badge needs ≤ 55%</p>
              </div>
              <div className="rounded-md border bg-muted/30 p-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold">10-day volume vs 50-day</p>
                <p className="font-mono text-sm font-bold mt-0.5">{m.vol_dryup_ratio != null ? `${Math.round(m.vol_dryup_ratio * 100)}%` : 'n/a'}</p>
                <p className="text-[10px] text-muted-foreground">badge needs ≤ 80%</p>
              </div>
            </div>
          )}

          <p className="text-[11px] text-muted-foreground leading-snug">
            {isRs
              ? (m.trend_template_pass
                ? 'This stock passes all 8 Trend Template rules; the RS rank orders the list. Recalculated nightly after the close.'
                : `This stock currently passes ${passed} of the 8 Trend Template rules, so it is not a Stage-2 name by Minervini’s definition. Recalculated nightly after the close.`)
              : 'A candidate flag, not a signal — open the chart and count two to four progressively shallower pullbacks before treating it as a VCP.'}{' '}
            <Link to="/learn/minervini-sepa" className="font-semibold text-amber-600 dark:text-amber-400 hover:underline" onClick={() => setOpen(false)}>Read the guide →</Link>
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
